import { useState, useEffect, useRef, useCallback } from 'react';
import {
  MsalProvider,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from '@azure/msal-react';
import Header from './components/Header';
import Result from './components/Result';
import LoginPage from './components/LoginPage';
import Menu from './components/Menu';
import Main from './components/Main';
import FetchOptionsMain from './api/FetchOptionsMain';
import FetchCalculationsMain from './api/FetchCalculationsMain';
import FetchPdp from './api/FetchPdp';
import GetAllPages from './api/GetAllPages';
import loadConfiguration from './api/LoadConfiguration';
import useDebounce from './function/Debounce';
import InitInput from './function/InitInput';
import handleInputChange from './function/HandleInputsChange';
import ProjectManager from './components/ProjectManager';
import handleProductTbhChange from './function/HandleProducttbhChange';
import SpiralVeyor from './drawing/SpiralVeyor';
import ChoosePdc from './components/ChoosePdc';
import SpiralMenu from './components/SpiralMenu';
import ProductToDisplay from './components/ProductToDisplay';
import ProductSizeMenu from './components/ProductSizeMenu';
import OptionSearchToolbar from './components/OptionSearchToolbar';
import {
  checkProductReady,
  checkProductState,
  updateProductMenuWarning,
} from './function/CheckProductReady';
import defaultDrawing from './drawing/defaultDrawing.json';
import resultToInputs from './function/resultToInputs';
import ProductMenu from './components/ProductMenu';
import NavigationButtons from './components/NavigationButtons';
import ProgressBar from './components/ProgressBar';
import { getSvlLockedPdpId } from './components/PdpComponent';
import { setupAxiosInterceptors } from './api/ApiConfig';
import { countConnections } from './function/HandleMLChange';
import { countMissingFieldByPage } from './function/CountMissingFieldByPage';
import { TableContainer } from './components/TableContainer';
import { NotificationProvider } from './components/NotificationContext';
import { NavigationProvider, useNavigation } from './function/navigation';
import './css/App.css';
import './css/notification.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/700.css';

function InsecureMain() {
  return (
    <div className="app-login">
      <LoginPage />
    </div>
  );
}

function SecureMain(props) {
  const { instance } = useMsal();
  const { options, setOptions, allPages, setAllPages } = props;
  const [amtIndex, setAmtIndex] = useState(null);

  const { nav, dispatch } = useNavigation();

  const [inputs, setInputs] = useState(InitInput);
  const [interceptorsReady, setInterceptorsReady] = useState(false);
  const [launchId, setLaunchId] = useState(null);
  const [result, setResult] = useState({});

  const debouncedInputs = useDebounce(inputs, 100);
  const debouncedOptions = useDebounce(options, 200);

  const [allPdc, setAllPdc] = useState([]);
  const pdcLoaded = allPdc.find((pdc) => pdc.id === inputs.pdc);

  const clickTriggeredRef = useRef(false);

  const [drawingBaseDimensions, setDrawingBaseDimensions] =
    useState(defaultDrawing);
  const [hasAllHeights, setHasAllHeights] = useState(false);
  const [basicInputsChecked, setBasicInputsChecked] = useState(false);
  const [baseInputsWarning, setBaseInputsWarning] = useState(null);
  const [isPhone, setIsPhone] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 800 : false
  );

  const [initNbrWindings, setInitNbrWindings] = useState(null);
  const [initFetchPdp, setInitFetchPdp] = useState(true);

  const [saveReady, setSaveReady] = useState(false);
  const [isCalculationsLoading, setIsCalculationsLoading] = useState(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);

  const [productState, setProductState] = useState([]);
  const [productMenuWarning, setProductMenuWarning] = useState({
    pdp: false,
    product: false,
  });

  const setCrmInfo = (newCrm) => {
    setInputs((prev) => ({
      ...prev,
      crm_info: {
        ...prev.crm_info,
        ...newCrm,
      },
    }));
  };

  const unitOptions = options.find((o) => o.field === 'unit_system')?.options;

  const setUnit = (unit) => {
    setInputs((prev) => ({
      ...prev,
      unit_system: unit,
    }));
  };

  const setRegion = (val) => {
    setInputs((prev) => ({
      ...prev,
      region: val,
    }));
  };

  const hasLoadedForId = useRef(new Set());

  useEffect(() => {
    if (launchId === null) {
      setInputs({
        ...InitInput,
        crm_info: {
          ...InitInput.crm_info,
          user_level: inputs.crm_info?.user_level,
        },
      });
      dispatch({ type: 'mainPage', payload: 'projectManager' });
    } else if (interceptorsReady) {
      if (!hasLoadedForId.current.has(launchId)) {
        loadConfiguration(
          launchId,
          inputs,
          setInputs,
          setOptions,
          setCrmInfo,
          setInitNbrWindings
        );
        hasLoadedForId.current.add(launchId);
      }
      if (inputs.pdc === null) {
        dispatch({ type: 'mainPage', payload: 'pdcPage' });
      } else {
        dispatch({ type: 'mainPage', payload: 'machine' });
      }
    }
    setResult({});
    setOptions([]);
    dispatch({ type: 'addWindingsSelected', payload: 0 });
  }, [interceptorsReady, launchId, inputs.pdc]);

  const setSpiralView = useCallback(
    (v) => dispatch({ type: 'spiralView', payload: v }),
    [dispatch]
  );

  const setFocusedInput = useCallback(
    (v) => dispatch({ type: 'focusedInput', payload: v }),
    [dispatch]
  );

  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 800);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const checkBasicInput = (inputs, hasAllHeights, result) => {
    if (!hasAllHeights) {
      setBaseInputsWarning(null);
      return false;
    }

    if (inputs.machine_type === null) {
      setBaseInputsWarning('A model is needed!');
      return false;
    }

    const track = inputs?.track;
    const isValidTrack =
      (typeof track === 'number' && track > 0) ||
      (typeof track === 'string' && track.trim() !== '') ||
      (track && typeof track === 'object' && Object.keys(track).length > 0);
    if (!isValidTrack) {
      setBaseInputsWarning('A track configuration is needed!');
      return false;
    }

    if (inputs.configuration === null) {
      setBaseInputsWarning('A configuration is needed!');
      return false;
    }

    if (
      inputs.sv_family !== 'SV-L' &&
      [null, undefined, 0, ''].includes(inputs.max_angle_center) &&
      [null, undefined, 0, ''].includes(inputs.max_angle_50mm_inner) &&
      !result?.max_angle_center
    ) {
      setBaseInputsWarning(
        'The maximum slope angle is undefined! Please fill in an angle in the "Stability" page'
      );
      return false;
    }

    const windOption = options.find((opt) => opt.field === 'nbr_windings');
    if (!windOption || (result?.nbr_windings ?? 0) < windOption.min) {
      setBaseInputsWarning(
        'The height difference is too small. At least one winding is required. Please check h1, h2 and h3, or consider alternative solutions such as Ambaveyors'
      );
      return false;
    }
    setBaseInputsWarning(null);
    return true;
  };

  useEffect(() => {
    setBasicInputsChecked(checkBasicInput(inputs, hasAllHeights, result));
  }, [
    inputs.machine_type,
    inputs.sv_family,
    hasAllHeights,
    inputs.configuration,
    inputs.max_angle_center,
    result.max_angle_center,
    inputs.max_angle_50mm_inner,
    result.nbr_windings,
  ]);

  useEffect(() => {
    setupAxiosInterceptors(instance);
    setInterceptorsReady(true);
  }, [instance]);

  useEffect(() => {
    const storedLaunchId = sessionStorage.getItem('launch_id');
    if (storedLaunchId !== null) {
      setLaunchId(storedLaunchId);
      sessionStorage.removeItem('launch_id');
      dispatch({ type: 'showOptions', payload: 'selectable' });
    }
  }, []);

  useEffect(() => {
    if (interceptorsReady && inputs.pdc) {
      FetchPdp(inputs.pdc, setInputs, setInitFetchPdp);
    } else if (inputs.pdc === 0) {
      handleInputChange('pdp', [], inputs, setInputs, setOptions);
      inputs.product_tbh?.forEach((product, index) =>
        handleProductTbhChange(index, 'pdp', null, setInputs)
      );
      dispatch({ type: 'productCurrentPage', payload: { menu: 'product' } });
    }
  }, [interceptorsReady, inputs.pdc]);

  useEffect(() => {
    if (initFetchPdp && inputs.max_angle_center) {
      setInputs((prevInputs) => {
        const productPdpIds = prevInputs.product_tbh
          .map((product) => product.pdp)
          .filter((pdpId) => pdpId != null);
        const hasProductPdpSelection = productPdpIds.length > 0;
        const updatedPdp = prevInputs.pdp.map((pdp) => {
          if (hasProductPdpSelection) {
            return {
              ...pdp,
              selected: productPdpIds.includes(pdp.id),
            };
          }
          return {
            ...pdp,
            selected: pdp.max_incline >= prevInputs.max_angle_center,
          };
        });

        return {
          ...prevInputs,
          pdp: updatedPdp,
        };
      });
      setInitFetchPdp(false);
    }
  }, [initFetchPdp, inputs.max_angle_center]);

  useEffect(() => {
    if (inputs.sv_family !== 'SV-L') {
      return;
    }

    const lockedPdpId = getSvlLockedPdpId(inputs.machine_type);
    if (lockedPdpId === null) {
      return;
    }

    setInputs((prevInputs) => {
      if (!prevInputs.pdp.some((pdp) => pdp.id === lockedPdpId)) {
        return prevInputs;
      }

      const updatedPdp = prevInputs.pdp.map((pdp) => ({
        ...pdp,
        selected: pdp.id === lockedPdpId,
      }));

      const updatedProductTbh = prevInputs.product_tbh.map((product) => ({
        ...product,
        pdp: lockedPdpId,
      }));

      const pdpAlreadyLocked = prevInputs.pdp.every(
        (pdp, idx) => pdp.selected === updatedPdp[idx].selected
      );
      const productAlreadyLocked = prevInputs.product_tbh.every(
        (product) => product.pdp === lockedPdpId
      );

      if (pdpAlreadyLocked && productAlreadyLocked) {
        return prevInputs;
      }

      return {
        ...prevInputs,
        pdp: updatedPdp,
        product_tbh: updatedProductTbh,
      };
    });
  }, [inputs.sv_family, inputs.machine_type, inputs.pdp, inputs.product_tbh]);

  useEffect(() => {
    const number_pdp = inputs.pdp?.filter((p) => p.selected).length ?? 0;
    handleInputChange(
      'number_pdp',
      number_pdp,
      inputs,
      setInputs,
      setOptions,
      setSpiralView
    );
  }, [inputs.pdp]);

  useEffect(() => {
    if (interceptorsReady) {
      setIsCalculationsLoading(true);
      Promise.resolve(
        FetchCalculationsMain(inputs, result, setResult, options)
      ).finally(() => setIsCalculationsLoading(false));
    }
  }, [interceptorsReady, debouncedOptions]);

  useEffect(() => {
    const driveList = result?.drive_list;
    const isNoDrivePlaceholder =
      Array.isArray(driveList) &&
      driveList.length === 1 &&
      Object.keys(driveList[0]).includes('no drive');

    if (isNoDrivePlaceholder) {
      if (inputs.drive !== null) {
        handleInputChange('drive', null, inputs, setInputs, setOptions);
      }
      return;
    }

    if (!Array.isArray(driveList) || driveList.length === 0) {
      if (inputs.drive !== null) {
        handleInputChange('drive', null, inputs, setInputs, setOptions);
      }
      return;
    }

    const selectedDriveId = inputs.drive?.id;
    const driveStillAvailable = driveList.some(
      (drive) => drive.id === selectedDriveId
    );

    if (inputs.drive === null || !driveStillAvailable) {
      handleInputChange('drive', driveList[0], inputs, setInputs, setOptions);
    }
  }, [result.drive_list, inputs.drive]);

  useEffect(() => {
    const newAllPages = GetAllPages(options, nav.showOptions, inputs.sv_family);
    if (
      !(
        allPages.length === newAllPages.length &&
        allPages.every((val, i) => val === newAllPages[i])
      )
    ) {
      setAllPages(newAllPages);
    }
  }, [options, nav.showOptions, inputs.sv_family]);

  useEffect(() => {
    if (nav.mainPage !== 'machine' || allPages.length === 0) return;
    if (allPages.includes(nav.currentPage)) return;

    const orderedPages = GetAllPages([], 'all', inputs.sv_family);
    const currentPageIndex = orderedPages.indexOf(nav.currentPage);

    let nextValidPage = allPages.find(
      (page) => orderedPages.indexOf(page) > currentPageIndex
    );

    if (!nextValidPage) {
      const previousPages = allPages.filter(
        (page) => orderedPages.indexOf(page) < currentPageIndex
      );
      nextValidPage = previousPages[previousPages.length - 1];
    }

    if (nextValidPage) {
      dispatch({ type: 'currentPage', payload: nextValidPage });
    }
  }, [allPages, nav.currentPage, nav.mainPage, dispatch]);

  useEffect(() => {
    if (interceptorsReady && debouncedInputs.pdc !== null) {
      setIsOptionsLoading(true);
      Promise.resolve(
        FetchOptionsMain(
          debouncedInputs,
          setInputs,
          setOptions,
          setFocusedInput,
          result,
          initNbrWindings,
          amtIndex,
          setAmtIndex
        )
      ).finally(() => setIsOptionsLoading(false));
    }
  }, [
    interceptorsReady,
    debouncedInputs,
    ...resultToInputs.map((field) => JSON.stringify(result?.[field])),
  ]);

  useEffect(() => {
    if (initNbrWindings !== null && result.nbr_windings) {
      if (initNbrWindings !== result.nbr_windings) {
        handleInputChange(
          'nbr_windings',
          initNbrWindings,
          inputs,
          setInputs,
          setOptions
        );
      }
      dispatch({
        type: 'addWindingsSelected',
        payload: Math.round(initNbrWindings - result.nbr_windings),
      });
      setInitNbrWindings(null);
    }
  }, [result.nbr_windings, initNbrWindings]);

  useEffect(() => {
    if (debouncedInputs.connection_conveyor !== null && options.length > 0) {
      setInputs((prevInputs) => {
        const counts = countConnections(
          options,
          debouncedInputs.connection_conveyor,
          debouncedInputs.ml_connection
        );
        return { ...prevInputs, ...counts };
      });
    }
  }, [debouncedInputs.connection_conveyor, debouncedInputs.ml_connection]);

  useEffect(() => {
    const missing = countMissingFieldByPage(
      options,
      inputs,
      result,
      'all',
      inputs.sv_family
    );
    const totalMissing = Object.values(missing).reduce((a, b) => a + b, 0);
    setSaveReady(
      totalMissing === 0 &&
        inputs.crm_info !== null &&
        inputs.crm_info.quotedetail_id !== null
    );
  }, [options, inputs, result]);

  useEffect(() => {
    const container = document.querySelector('.parameters-column');
    if (!container || !clickTriggeredRef.current) return;

    const pageEl = container.querySelector(
      `.page-container[data-page="${nav.currentPage}"]`
    );
    if (!pageEl) return;

    const offset = 30;

    container.scrollTo({
      top: Math.max(0, pageEl.offsetTop - offset),
      behavior: 'smooth',
    });

    const timer = setTimeout(() => {
      clickTriggeredRef.current = false;
    }, 350);

    return () => clearTimeout(timer);
  }, [nav.currentPage]);

  useEffect(() => {
    const parameterColumn = document.querySelector('.parameters-column');
    if (!parameterColumn) return;

    const pages = Array.from(
      parameterColumn.querySelectorAll('.page-container')
    );

    const computeCurrentPage = () => {
      if (clickTriggeredRef.current) return;

      const containerRect = parameterColumn.getBoundingClientRect();
      let closestPage = null;
      let minDistance = Infinity;

      pages.forEach((el) => {
        const elRect = el.getBoundingClientRect();
        const distance = Math.abs(elRect.top - containerRect.top);
        if (distance < minDistance) {
          minDistance = distance;
          closestPage = el;
        }
      });

      if (closestPage) {
        const page = closestPage.getAttribute('data-page');
        if (page !== nav.currentPage) {
          dispatch({ type: 'currentPage', payload: page });
        }
      }
    };

    // Initial run
    computeCurrentPage();

    // Scroll/resize handler
    const onScroll = () => {
      requestAnimationFrame(computeCurrentPage);
    };

    parameterColumn.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      parameterColumn.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [allPages, nav.currentPage, nav.mainPage, options]);

  useEffect(() => {
    if (nav.currentPage === 'Size')
      dispatch({ type: 'spiralView', payload: 'product' });
    else if (nav.currentPage === 'Flow')
      dispatch({ type: 'spiralView', payload: 'home' });
  }, [nav.currentPage]);

  useEffect(() => {
    if (!nav.focusedProduct && nav.productSizeView !== 'windings')
      dispatch({ type: 'productSizeView', payload: 'windings' });
  }, [nav.productSizeView, nav.focusedProduct, dispatch]);

  useEffect(() => {
    const allHeight = Boolean(
      inputs.h1 &&
        (inputs.h2 || result.h2) &&
        (inputs.h3 || result.h3) &&
        parseInt(result.h3 || inputs.h3) > parseInt(inputs.h1, 10)
    );
    if (!allHeight) {
      dispatch({ type: 'spiralView', payload: 'home' });
    }
    setHasAllHeights(allHeight);
  }, [inputs.h1, inputs.h2, inputs.h3, result.h2, result.h3, dispatch]);

  useEffect(() => {
    const baseDim = Object.fromEntries(
      Object.entries(defaultDrawing).map(([k, v]) => [
        k,
        basicInputsChecked
          ? typeof v === 'number'
            ? Number(result?.[k] ?? inputs?.[k] ?? v)
            : (result?.[k] ?? inputs?.[k] ?? v)
          : v,
      ])
    );
    setDrawingBaseDimensions(baseDim);
  }, [inputs, result, basicInputsChecked]);

  useEffect(() => {
    if (!isPhone) return;
    const onPointerDown = (e) => {
      const menuEl = document.querySelector('.glass-menu.phone');
      if (menuEl && nav.phoneMenu === 'menu' && !menuEl.contains(e.target)) {
        dispatch({ type: 'phoneMenu', payload: 'params' });
      }
    };
    document.addEventListener('pointerdown', onPointerDown, { passive: true });
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isPhone, nav.phoneMenu, dispatch]);

  useEffect(() => {
    if (nav.focusedInput !== null) {
      const indexStr = nav.focusedInput.split('_').at(-1);
      const index = indexStr !== null ? Number(indexStr) : null;
      if (
        index !== null &&
        checkProductReady(
          inputs.product_tbh?.[index] ?? [],
          inputs.product_flow
        )
      )
        dispatch({ type: 'focusedProduct', payload: index + 1 });
      else if (['h1', 'h2', 'h3'].includes(nav.focusedInput))
        dispatch({ type: 'spiralView', payload: 'home' });
    }
  }, [nav.focusedInput, inputs.product_tbh, dispatch]);

  useEffect(() => {
    const productState =
      Array.isArray(inputs.product_tbh) && inputs.product_tbh.length
        ? checkProductState(
            inputs.product_tbh,
            inputs.pdp,
            result,
            inputs.sv_family,
            inputs.product_flow
          )
        : [];

    setProductState(productState);

    if (inputs && options && result) {
      setProductMenuWarning(
        updateProductMenuWarning(productState, inputs, options, result)
      );
    }
  }, [result, inputs.product_flow]);

  const hasModeWarning =
    nav.mainPage === 'product'
      ? !saveReady
      : productMenuWarning.pdp || productMenuWarning.product;

  const toggleMode = () => {
    dispatch({
      type: 'mainPage',
      payload: nav.mainPage === 'product' ? 'machine' : 'product',
    });
  };

  return (
    <div className="app">
      <Header
        inputs={inputs}
        setRegion={setRegion}
        pdcLoaded={pdcLoaded}
        crmInfo={inputs.crm_info}
        setCrmInfo={setCrmInfo}
        unitOptions={unitOptions}
        setUnit={setUnit}
        hidePhoneMenu={
          nav.mainPage === 'projectManager' || nav.mainPage === 'pdc'
        }
        hasAllHeights={hasAllHeights}
      />
      {nav.mainPage === 'projectManager' ? (
        <div className="glass-project-manager">
          <ProjectManager
            interceptorsReady={interceptorsReady}
            setLaunchId={setLaunchId}
            level={inputs.crm_info.user_level}
            setCrmInfo={setCrmInfo}
          />
        </div>
      ) : nav.mainPage === 'pdcPage' ? (
        <ChoosePdc
          allPdc={allPdc}
          setAllPdc={setAllPdc}
          inputs={inputs}
          setInputs={setInputs}
          interceptorsReady={interceptorsReady}
          setLaunchId={setLaunchId}
        />
      ) : (
        options.length > 0 && (
          <div className="glass-content-container">
            {(!isPhone ||
              nav.phoneMenu === 'params' ||
              nav.phoneMenu === 'menu') && (
              <div className="main-block">
                <ProgressBar setLaunchId={setLaunchId} />
                <div className="parameters-block">
                  <div className={`glass-menu ${isPhone ? 'phone' : ''}`}>
                    <div
                      className={[
                        'product-menu-item',
                        'toggle',
                        nav.mainPage === 'product' ? 'active' : '',
                        hasModeWarning ? 'warning' : '',
                        nav.expandedMenu && nav.mainPage === 'machine'
                          ? 'expanded'
                          : '',
                      ].join(' ')}
                      onClick={toggleMode}
                    >
                      {nav.mainPage === 'product' ? (
                        <div className="product-menu-custom-content">←</div>
                      ) : (
                        <img
                          src={`${process.env.PUBLIC_URL}/icons/add-product.png`}
                          alt="mode-logo"
                          className="product-menu-icon"
                        />
                      )}
                      {nav.expandedMenu && nav.mainPage === 'machine' && (
                        <div className="group-name">Go to product</div>
                      )}
                      <span className="product-menu-text">
                        {nav.mainPage === 'product'
                          ? 'Back to configuration'
                          : 'Go to product'}
                      </span>
                    </div>
                    {nav.mainPage === 'product' ? (
                      <ProductMenu
                        inputs={inputs}
                        options={options}
                        productMenuWarning={productMenuWarning}
                      />
                    ) : (
                      <Menu
                        options={options}
                        inputs={inputs}
                        result={result}
                        allPages={allPages}
                        clickTriggeredRef={clickTriggeredRef}
                      />
                    )}
                  </div>
                  <div className="parameters-column">
                    {nav.mainPage === 'machine' && (
                      <OptionSearchToolbar
                        options={options}
                        amtIndex={amtIndex}
                        clickTriggeredRef={clickTriggeredRef}
                        saveReady={saveReady}
                        svFamily={inputs.sv_family}
                      />
                    )}
                    <NavigationButtons
                      allPages={allPages}
                      clickTriggeredRef={clickTriggeredRef}
                      isProduct={options?.some((o) => o.field == 'product_tbh')}
                    />
                    {nav.mainPage === 'product' ? (
                      <TableContainer
                        inputs={inputs}
                        setInputs={setInputs}
                        options={options}
                        setOptions={setOptions}
                        result={result}
                        productState={productState}
                        clickTriggeredRef={clickTriggeredRef}
                      />
                    ) : (
                      allPages.map((page) => (
                        <div
                          key={page}
                          className="page-container"
                          data-page={page}
                        >
                          <Main
                            currentPage={page}
                            inputs={inputs}
                            setInputs={setInputs}
                            options={options}
                            setOptions={setOptions}
                            result={result}
                            clickTriggeredRef={clickTriggeredRef}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
            {!isPhone && (
              <div className="empty-column">
                {(!isPhone || nav.phoneMenu === 'spiral') && (
                  <div
                    className={`spiral-veyor-canvas ${basicInputsChecked ? '' : 'height-diff'}`}
                  >
                    {nav.spiralView && (
                      <SpiralVeyor
                        inputs={inputs}
                        result={result}
                        drawingBaseDimensions={drawingBaseDimensions}
                      />
                    )}
                  </div>
                )}
                {(!isPhone || nav.phoneMenu === 'spiral') &&
                  basicInputsChecked && <SpiralMenu />}
                {(!isPhone || nav.phoneMenu === 'spiral') && (
                  <ProductToDisplay
                    inputs={inputs}
                    productState={productState}
                  />
                )}
                {(!isPhone || nav.phoneMenu === 'spiral') &&
                  nav.spiralView === 'product' &&
                  nav.focusedProduct > 0 && <ProductSizeMenu />}
              </div>
            )}
            {(!isPhone || nav.phoneMenu === 'summary') && basicInputsChecked ? (
              <Result
                result={result}
                inputs={inputs}
                setInputs={setInputs}
                options={options}
                setOptions={setOptions}
                saveReady={basicInputsChecked}
                clickTriggeredRef={clickTriggeredRef}
                setLaunchId={setLaunchId}
                isFetchingData={isCalculationsLoading || isOptionsLoading}
              />
            ) : (
              baseInputsWarning && (
                <div className="tooltip-warning">
                  <div className={`tooltip warning bottom right`}>
                    {baseInputsWarning}
                  </div>
                </div>
              )
            )}
          </div>
        )
      )}
    </div>
  );
}

function NavigationBootstrap({ children }) {
  const [options, setOptions] = useState([]);
  const [allPages, setAllPages] = useState(GetAllPages(options, 'priced'));

  const isPhone =
    typeof window !== 'undefined' ? window.innerWidth < 800 : false;

  const initialNav = {
    mainPage: 'pdc',
    expandedMenu: false,
    currentPage: allPages[0],
    productCurrentPage: { menu: 'pdp', tab: 'PDP' },
    showOptions: 'priced',
    focusedInput: null,
    focusedProduct: null,
    addWindingsSelected: 0,
    spiralView: 'home',
    productSizeView: 'windings',
    feedbackPanelOpen: false,
    phoneMenu: isPhone ? 'params' : false,
  };

  return (
    <NavigationProvider initial={initialNav}>
      {children({
        options,
        setOptions,
        allPages,
        setAllPages,
      })}
    </NavigationProvider>
  );
}

function MainContent() {
  return (
    <>
      <AuthenticatedTemplate>
        <NavigationBootstrap>
          {(navProps) => <SecureMain {...navProps} />}
        </NavigationBootstrap>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <InsecureMain />
      </UnauthenticatedTemplate>
    </>
  );
}

function App({ instance }) {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const launch_id = params.get('launch_id');
    if (launch_id !== null) {
      sessionStorage.setItem('launch_id', launch_id);
    }
  }, []);

  return (
    <MsalProvider instance={instance}>
      <NotificationProvider>
        <MainContent />
      </NotificationProvider>
    </MsalProvider>
  );
}

export default App;
